import * as THREE from 'three'; // 导入three.js核心库
import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d';
import { StatsModule } from '../../compontent/stats';
import { getElementSize, getGSFileExt, getRotationMatrix, getWindowSize } from '../../../utils';

export default class SimpleViewer {
  private container: HTMLElement | null;
  private scaleRenderer: boolean;
  private threeScene: THREE.Scene | null;
  private viewers: Map<string, any>;
  private urls: Array<{ url: string; show: boolean; position?: number[]; rotation?: number[]; scale?: number[] }>;
  private stats: StatsModule;
  private renderer?: THREE.WebGLRenderer;
  private camera?: THREE.PerspectiveCamera;
  private controls: any;

  constructor(container: string | null = null, scale = false) {
    this.container = container ? document.querySelector(container) : null;
    this.scaleRenderer = scale;
    this.threeScene = null;
    this.viewers = new Map();
    this.urls = [];
    this.stats = new StatsModule({
      containerId: 'js-simple-viewer',
      type: 0,
      style: { position: 'absolute', top: '0' },
    });
    // this.stats.show();
  }

  async init(url) {
    try {
      if (url === '' || !url) return;
      const configUrl = url;

      const config = await this.getSceneMataFile(configUrl);
      // const rootElement = null;
      const { renderer, renderWidth, renderHeight } = this.setupRenderer(this.container);
      this.renderer = renderer;
      this.camera = this.setupCamera(renderWidth, renderHeight);
      this.threeScene = this.setupThreeScene();
      this.controls = this.setupControls(this.camera, renderer);

      // 如果指定了容器，添加到选项中
      // if (this.container) {
      //   rootElement = document.querySelector(this.container);
      //   // renderer = this.initRenderer(rootElement);
      //   // renderer && rootElement.appendChild(renderer.domElement);
      // }
      const viewerOptions = {
        // rootElement,
        cameraUp: config?.cameraInfo?.lookUp || config?.lookUp,
        initialCameraPosition: config?.cameraInfo?.position || config?.position,
        initialCameraLookAt: config?.cameraInfo?.lookAt || config?.lookAt,
        sphericalHarmonicsDegree: config?.sphericalHarmonicsDegree >= 0 ? config.sphericalHarmonicsDegree : 2,
        sharedMemoryForWorkers: false,
        // renderer,
        showInfo: true,
        showLoadingUI: false,
        ...config?.gaussianSplats3DOption,
      };

      console.log('viewerOptions', viewerOptions);
      // this.threeScene = new THREE.Scene();
      // this.viewer = new GaussianSplats3D.DropInViewer(viewerOptions);

      // 判断 config.url 是不是数组
      if (config.url instanceof Array) {
        this.urls = config.url.map((item) => ({
          url: item.path,
          show: false,
          position: item?.position,
          rotation: getRotationMatrix(item?.rotation),
          scale: item?.scale,
        }));
      } else {
        this.urls = [
          {
            url: config.url,
            show: false,
            position: config?.splashInfo?.position,
            rotation: getRotationMatrix(config?.splashInfo?.rotation),
            scale: config?.splashInfo?.scale,
          },
        ];
      }

      const start = new Date().getTime();
      const viewer = new GaussianSplats3D.DropInViewer({ dynamicScene: true });
      this.viewers.set(this.urls[0].url, viewer);
      await viewer.addSplatScene(this.urls[0].url, {
        format: getGSFileExt(this.urls[0].url),
        progressiveLoad: true,
        showLoadingUI: false,
        position: this.urls[0].position,
        rotation: this.urls[0].rotation,
        scale: this.urls[0].scale,
      });
      this.threeScene.add(viewer);
      console.log('init time', new Date().getTime() - start);
      this.changeUrlState(this.urls[0].url, true);
      // viewer.start();
      this.initThreeAxesHelper();
      this.update();
    } catch (error) {
      console.error('Failed to initialize GSViewer:', error);
    }
  }
  setupRenderer(container) {
    const wrapper = container ? document.querySelector(container) : document.body;
    const size = getElementSize(wrapper);
    console.log('size', size);
    const renderWidth = size.width;
    const renderHeight = size.height;

    const rootElement = document.createElement('div');
    rootElement.style.width = `${renderWidth}px`;
    rootElement.style.height = `${renderHeight}px`;
    rootElement.style.position = 'relative';
    rootElement.style.left = '50%';
    rootElement.style.top = '50%';
    rootElement.style.transform = 'translate(-50%, -50%)';
    wrapper.appendChild(rootElement);

    const renderer = new THREE.WebGLRenderer({
      antialias: false,
    });
    renderer.setClearColor(new THREE.Color(0x000000), 0.0);
    renderer.setSize(renderWidth, renderHeight);
    rootElement.appendChild(renderer.domElement);

    return {
      renderer,
      renderWidth,
      renderHeight,
    };
  }

  setupCamera(renderWidth, renderHeight) {
    const camera = new THREE.PerspectiveCamera(65, renderWidth / renderHeight, 0.1, 500);
    camera.position.copy(new THREE.Vector3().fromArray([-1, -4, 6]));
    camera.lookAt(new THREE.Vector3().fromArray([0, 4, -0]));
    camera.up = new THREE.Vector3().fromArray([0, -1, -0.6]).normalize();
    return camera;
  }

  setupThreeScene() {
    const threeScene = new THREE.Scene();
    const boxColor = 0xbbbbbb;
    const boxGeometry = new THREE.BoxGeometry(2, 2, 2);
    const boxMesh = new THREE.Mesh(boxGeometry, new THREE.MeshBasicMaterial({ color: boxColor }));
    threeScene.add(boxMesh);
    boxMesh.position.set(3, 2, 2);
    return threeScene;
  }

  setupControls(camera, renderer) {
    const controls = new GaussianSplats3D.OrbitControls(camera, renderer.domElement);
    controls.rotateSpeed = 0.5;
    controls.maxPolarAngle = Math.PI * 0.75;
    controls.minPolarAngle = 0.1;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    return controls;
  }

  initThreeAxesHelper() {
    console.log('initThreeAxesHelper', this.threeScene);
    const axesHelper = new THREE.AxesHelper(50);
    this.threeScene!.add(axesHelper);
  }

  initRenderer(container) {
    if (!this.scaleRenderer) return null;
    // eslint-disable-next-line no-param-reassign
    container.style.transform = 'scale(2)';
    // eslint-disable-next-line no-param-reassign
    container.style.transformOrigin = '0 0';
    const renderer = new THREE.WebGLRenderer({
      antialias: false,
      precision: 'highp',
    });
    // renderer.setPixelRatio(this.devicePixelRatio);
    renderer.autoClear = true;
    renderer.setClearColor(new THREE.Color(0x000000), 0.0);
    const size = container ? getElementSize(container) : getWindowSize();
    console.log('size', size);
    renderer.setSize(size.width / 2, size.height / 2);
    return renderer;
  }

  showInfoPanel() {
    // this.viewer.showInfo = status;
    // status ? this.viewer.infoPanel.show() : this.viewer.infoPanel.hide();
  }

  getUrls() {
    return this.urls;
  }

  update() {
    // console.log("update");
    requestAnimationFrame(this.update.bind(this));
    this.stats.stats!.update();
    this.controls.update();
    this.renderer!.render(this.threeScene!, this.camera!);
  }

  async add(index) {
    const start = new Date().getTime();
    // await this.viewer.addSplatScene(this.urls[index].url, {
    //   format: getGSFileExt(this.urls[index].url),
    //   progressiveLoad: true,
    //   showLoadingUI: false,
    //   position: this.urls[index].position,
    //   rotation: this.urls[index].rotation,
    //   scale: this.urls[index].scale,
    // });
    const viewer = new GaussianSplats3D.DropInViewer({ dynamicScene: true });
    this.viewers.set(this.urls[index].url, viewer);
    await viewer.addSplatScene(this.urls[index].url, {
      format: getGSFileExt(this.urls[index].url),
      progressiveLoad: true,
      showLoadingUI: false,
      position: this.urls[index].position,
      rotation: this.urls[index].rotation,
      scale: this.urls[index].scale,
    });
    this.threeScene!.add(viewer);
    console.log('add time', new Date().getTime() - start);
    // console.log('add', this.viewer);
    this.changeUrlState(this.urls[index].url, true);
    return this.urls;
  }

  remove(index) {
    let splatIndex = -1;
    for (let i = 0; i <= index && i < this.urls.length; i++) {
      if (this.urls[i].show === true) {
        splatIndex += 1;
      }
    }
    // @ts-ignore
    this.viewer.removeSplatScene(splatIndex, false);
    this.changeUrlState(this.urls[index].url, false);
  }

  changeUrlState(path, show) {
    const urlItem = this.urls.find((item) => item.url === path);
    // @ts-ignore
    urlItem.show = show;
  }

  // 添加重置相机方法
  resetCamera() {
    // @ts-ignore
    if (this.viewer?.resetCamera) {
      // @ts-ignore
      this.viewer.resetCamera();
    }
  }

  // 添加销毁方法
  dispose() {
    // @ts-ignore
    if (this.viewer) {
      // this.viewer.dispose();
      // @ts-ignore
      this.viewer = null;
    }
  }

  async getSceneMataFile(url) {
    const requestFile = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'reload',
    });
    if (requestFile.status === 200) {
      const content = await requestFile.json();
      console.log('content', content);
      return content;
    }
  }
}
